import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Grid,
  TextField,
  Button,
  Chip,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  Rating,
} from "@mui/material";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {
  ResolveTenancyCondition,
  RespondToDamageClaim,
  conditionBody,
} from "../redux/reducer/PropertyCondition";
import {
  GetTenantSettlementBills,
  SubmitSettlementReceipt,
  settlementBody,
} from "../redux/reducer/SettlementBill";
import {
  SubmitReview,
  GetTenantReviews,
  reviewBody,
} from "../redux/reducer/PropertyReview";

const HEADER_GRADIENT = "linear-gradient(135deg, #0f766e 0%, #0891b2 100%)";
const money = (n) => `Rs ${Number(n || 0).toLocaleString("en-US")}`;

const PhotoGrid = ({ photos, empty }) => {
  if (!photos?.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        {empty}
      </Typography>
    );
  }
  return (
    <Grid container spacing={1}>
      {photos.map((p, i) => (
        <Grid item xs={4} key={p.publicId || i}>
          <Box
            component="a"
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: "block" }}
          >
            <Box
              component="img"
              src={p.url}
              alt={`Photo ${i + 1}`}
              sx={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 1.5 }}
            />
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

export default function TenantPropertyConditionDialog({
  open,
  onClose,
  tenantId,
  flatId,
  flatName,
}) {
  const dispatch = useDispatch();
  const { condition, tenancyRequestId, moveInStatus, loading } = useSelector(
    (state) => state.propertyCondition
  );
  const { bills } = useSelector((state) => state.settlementBill);
  const { mine: myReviews } = useSelector((state) => state.propertyReview);

  const [responseMsg, setResponseMsg] = useState("");
  const [responding, setResponding] = useState(false);

  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);

  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    if (open && tenantId && flatId) {
      dispatch(ResolveTenancyCondition({ tenantId, flatId }));
      dispatch(GetTenantSettlementBills({ tenantId }));
      dispatch(GetTenantReviews({ tenantId }));
    }
  }, [open, tenantId, flatId, dispatch]);

  const bill = bills.find((b) => b.tenancyRequestId === tenancyRequestId);
  const existingReview = myReviews.find((r) => r.tenancyRequestId === tenancyRequestId);
  const canRespond =
    condition?.status === "CLAIMED" &&
    condition.respondByDate &&
    new Date() <= new Date(condition.respondByDate);
  const canReview = moveInStatus === "moved_out" && !existingReview;

  const handleRespond = async () => {
    if (!responseMsg.trim()) {
      toast.warning("Please write your response.");
      return;
    }
    setResponding(true);
    try {
      const res = await dispatch(
        RespondToDamageClaim({ id: condition._id, message: responseMsg })
      );
      const body = conditionBody(res);
      if (body?.success) {
        toast.success("Your response has been sent to the owner.");
        setResponseMsg("");
      } else {
        toast.error(body?.message || "Failed to send response");
      }
    } finally {
      setResponding(false);
    }
  };

  const handleUploadReceipt = async () => {
    if (!receiptFile) {
      toast.warning("Please select a receipt to upload.");
      return;
    }
    setUploadingReceipt(true);
    try {
      const res = await dispatch(
        SubmitSettlementReceipt({ id: bill._id, receipt: receiptFile })
      );
      const body = settlementBody(res);
      if (body?.success) {
        toast.success("Receipt submitted for verification.");
        setReceiptFile(null);
      } else {
        toast.error(body?.message || "Failed to submit receipt");
      }
    } finally {
      setUploadingReceipt(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!rating) {
      toast.warning("Please select a rating.");
      return;
    }
    setSubmittingReview(true);
    try {
      const res = await dispatch(
        SubmitReview({ tenancyRequestId, rating, comment })
      );
      const body = reviewBody(res);
      if (body?.success) {
        toast.success(body.message || "Review submitted.");
        dispatch(GetTenantReviews({ tenantId }));
      } else {
        toast.error(body?.message || "Failed to submit review");
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            background: HEADER_GRADIENT,
            color: "#fff",
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <HomeIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Property Condition
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {flatName || "This property"}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Move-in photos (before)
            </Typography>
            <PhotoGrid photos={condition?.moveInPhotos} empty="No move-in photos on record." />

            <Divider sx={{ my: 2.5 }} />

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Move-out photos (after)
            </Typography>
            <PhotoGrid photos={condition?.moveOutPhotos} empty="No move-out photos on record." />

            {condition?.status && condition.status !== "NONE" && (
              <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={condition.status} color="warning" />
                  <Typography variant="body2" fontWeight={700}>
                    Claimed: {money(condition.claimedAmount)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {condition.damageDescription}
                </Typography>

                {canRespond && (
                  <Box sx={{ mt: 1.5 }}>
                    <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
                      Respond by {dayjs(condition.respondByDate).format("DD MMM YYYY")} —
                      if you don't, this claim stands as filed.
                    </Alert>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      placeholder="Explain your side…"
                      value={responseMsg}
                      onChange={(e) => setResponseMsg(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={responding}
                      onClick={handleRespond}
                    >
                      {responding ? "Sending…" : "Send response"}
                    </Button>
                  </Box>
                )}

                {condition.tenantResponse?.message && (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                    Your response: "{condition.tenantResponse.message}"
                  </Alert>
                )}

                {["RESOLVED", "EXPIRED"].includes(condition.status) && (
                  <Alert severity="success" variant="outlined" sx={{ mt: 1.5 }}>
                    Finalized at {money(condition.finalAmount)}
                    {condition.decidedBy === "auto"
                      ? " — you did not respond within the 7-day window."
                      : "."}
                  </Alert>
                )}
              </Box>
            )}

            {bill && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Final settlement bill
                </Typography>
                <Typography variant="body2">
                  Amount owed beyond your deposit: <strong>{money(bill.amount)}</strong>
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {bill.breakdown?.map((b, i) => (
                    <Typography key={i} variant="caption" color="text.secondary">
                      {b.label}: {money(b.amount)}
                    </Typography>
                  ))}
                </Stack>
                <Chip size="small" label={bill.status} sx={{ mt: 1 }} />

                {["PENDING", "REJECTED"].includes(bill.status) && (
                  <Box sx={{ mt: 1.5 }}>
                    {bill.status === "REJECTED" && bill.rejectionReason && (
                      <Alert severity="error" variant="outlined" sx={{ mb: 1 }}>
                        Previous receipt rejected: {bill.rejectionReason}
                      </Alert>
                    )}
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      startIcon={<AttachFileIcon fontSize="small" />}
                    >
                      {receiptFile ? receiptFile.name : "Select receipt"}
                      <input
                        type="file"
                        hidden
                        accept="image/*,application/pdf"
                        onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                      />
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ ml: 1 }}
                      disabled={uploadingReceipt}
                      onClick={handleUploadReceipt}
                    >
                      {uploadingReceipt ? "Uploading…" : "Submit receipt"}
                    </Button>
                  </Box>
                )}
                {bill.status === "SUBMITTED" && (
                  <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                    Awaiting owner verification.
                  </Alert>
                )}
                {bill.status === "VERIFIED" && (
                  <Alert severity="success" variant="outlined" sx={{ mt: 1.5 }}>
                    Payment verified — this is cleared.
                  </Alert>
                )}
              </>
            )}

            {(canReview || existingReview) && (
              <>
                <Divider sx={{ my: 2.5 }} />
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Rate this property
                </Typography>
                {existingReview ? (
                  <>
                    <Rating value={existingReview.rating} readOnly />
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {existingReview.comment}
                    </Typography>
                    <Chip
                      size="small"
                      sx={{ mt: 1 }}
                      label={
                        existingReview.status === "APPROVED"
                          ? "Published"
                          : existingReview.status === "REJECTED"
                          ? "Not approved"
                          : "Awaiting review"
                      }
                    />
                    {existingReview.ownerResponse?.message && (
                      <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                        Owner's response: "{existingReview.ownerResponse.message}"
                      </Alert>
                    )}
                  </>
                ) : (
                  <>
                    <Rating
                      value={rating}
                      onChange={(e, v) => setRating(v)}
                      size="large"
                    />
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      size="small"
                      placeholder="What was it like living here?"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      sx={{ mt: 1, mb: 1 }}
                    />
                    <Button
                      size="small"
                      variant="contained"
                      disabled={submittingReview}
                      onClick={handleSubmitReview}
                    >
                      {submittingReview ? "Submitting…" : "Submit review"}
                    </Button>
                  </>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
